/**
 * #38 · Catálogo de productos.
 *
 * Listado keyset + alta. La edición y la baja viven en el detalle
 * (`ProductDetailPage`): son operaciones sobre UN producto y meterlas en la
 * fila de una tabla de doscientos SKU es cómo se borra el equivocado.
 *
 * `POST /api/v1/products` exige `analyst`; el botón se esconde por debajo de
 * ese rol. Eso es ergonomía, no autorización — la API responde 403 igual.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { useKeysetQuery } from '@/api/hooks';
import { productKeys } from '@/api/keys';
import { useApi } from '@/api/useApi';
import type { Schemas } from '@/api/types';
import { useAuth } from '@/auth/useAuth';
import { useI18n } from '@/i18n/useI18n';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { EmptyState, ErrorState, LoadingState } from '@/ui/states';
import { Table, Td, Th } from '@/ui/Table';
import { TIER_LABEL_KEYS, TIER_TONE, isSecurityTier, riskTone } from './catalog';
import { ProductForm, type ProductDraft } from './ProductForm';
import { Dialog } from './ui/Dialog';

type Product = Schemas['ProductOut'];

const PAGE_SIZE = 50;

export function ProductsPage(): ReactNode {
  const { t, fmt } = useI18n();
  const { can } = useAuth();
  const api = useApi();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);

  const products = useKeysetQuery<Product>(
    productKeys.list({ limit: PAGE_SIZE }),
    '/api/v1/products',
    undefined,
    { limit: PAGE_SIZE },
  );
  const { query } = products;

  const create = useMutation<Product, unknown, ProductDraft>({
    mutationFn: (draft) => api.post<Product>('/api/v1/products', draft),
    onSuccess: () => {
      setCreating(false);
      void queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });

  return (
    <div className="mx-auto max-w-6xl">
      <Card>
        <CardHeader
          title={t('products.title')}
          description={t('products.description')}
          actions={
            <>
              {products.items.length > 0 && (
                <Badge tone="neutral">
                  {t('products.count', { count: products.items.length })}
                </Badge>
              )}
              {can('analyst') && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    create.reset();
                    setCreating(true);
                  }}
                >
                  {t('products.new')}
                </Button>
              )}
            </>
          }
        />

        {query.isPending && <LoadingState rows={6} />}

        {query.isError && (
          <CardBody>
            <ErrorState
              error={query.error}
              onRetry={() => {
                void query.refetch();
              }}
            />
          </CardBody>
        )}

        {!query.isPending && !query.isError && products.items.length === 0 && (
          <EmptyState title={t('products.empty')} detail={t('products.empty.detail')} />
        )}

        {products.items.length > 0 && (
          <>
            <Table caption={t('products.description')}>
              <thead>
                <tr>
                  <Th>{t('products.column.sku')}</Th>
                  <Th>{t('products.column.name')}</Th>
                  <Th>{t('products.column.gtin')}</Th>
                  <Th>{t('products.column.tier')}</Th>
                  <Th>{t('products.column.risk')}</Th>
                  <Th>{t('products.column.status')}</Th>
                </tr>
              </thead>
              <tbody>
                {products.items.map((product) => (
                  <tr key={product.id}>
                    <Td>
                      <Link
                        to={`/products/${String(product.id)}`}
                        className="font-mono font-medium text-accent underline underline-offset-2"
                      >
                        {product.sku}
                      </Link>
                    </Td>
                    <Td className="font-medium">{product.name}</Td>
                    <Td className="font-mono text-ink-muted">
                      {product.gtin ?? t('products.gtin.none')}
                    </Td>
                    <Td>
                      <Badge tone={TIER_TONE}>
                        {isSecurityTier(product.security_tier)
                          ? t(TIER_LABEL_KEYS[product.security_tier])
                          : product.security_tier}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge tone={riskTone(product.risk_score)}>
                        {fmt.number(product.risk_score)}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge tone={product.is_active ? 'verified' : 'neutral'}>
                        {product.is_active ? t('products.active') : t('products.inactive')}
                      </Badge>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <CardBody className="flex items-center justify-center border-t border-border">
              {products.hasMore ? (
                <Button size="sm" onClick={products.loadMore} disabled={products.isLoadingMore}>
                  {products.isLoadingMore ? t('state.loadingMore') : t('state.loadMore')}
                </Button>
              ) : (
                <p className="text-xs text-ink-subtle">{t('state.allLoaded')}</p>
              )}
            </CardBody>
          </>
        )}
      </Card>

      <Dialog
        open={creating}
        onClose={() => {
          setCreating(false);
        }}
        title={t('products.new.title')}
        description={t('products.new.description')}
      >
        <ProductForm
          onSubmit={(draft) => {
            create.mutate(draft);
          }}
          onCancel={() => {
            setCreating(false);
          }}
          isSubmitting={create.isPending}
          error={create.error}
        />
      </Dialog>
    </div>
  );
}
